import React from 'react';
import { CertQuestionMCQSingle } from '@unity-learning/shared';
import CodeMirror from '@uiw/react-codemirror';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { useTheme } from '../../contexts/ThemeContext';
import { ContextCode } from './ContextCode';

// Light theme for CodeMirror
const lightTheme = EditorView.theme({
    '&': { backgroundColor: '#f8f9fa' },
    '.cm-gutters': { backgroundColor: '#f0f0f0', borderRight: '1px solid #ddd' },
    '.cm-content': { caretColor: '#333' },
    '.cm-line': { color: '#333' },
});
interface QuestionMCQSingleProps {
    question: Omit<CertQuestionMCQSingle, 'validation'>;
    value: string | null;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const QuestionMCQSingle: React.FC<QuestionMCQSingleProps> = ({
    question,
    value,
    onChange,
    disabled = false
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { content } = question;
    const { options, context_code } = content;

    return (
        <div className="w-full space-y-4">
            {/* Context Code (if present) */}
            {context_code && (
                <div className="mb-6">
                    <ContextCode code={context_code} />
                </div>
            )}

            {/* Options */}
            <div className="space-y-3">
                {options.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        onClick={() => !disabled && onChange(option.id)}
                        disabled={disabled}
                        className={`
              w-full flex items-start p-4 rounded-lg border-2 transition-all duration-200 text-left
              ${value === option.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                            }
              ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
            `}
                    >
                        {/* Radio Circle */}
                        <div className={`
              flex-shrink-0 w-5 h-5 rounded-full border-2 mr-4 mt-0.5
              flex items-center justify-center
              ${value === option.id
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-400 dark:border-gray-500'
                            }
            `}>
                            {value === option.id && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                        </div>

                        {/* Option Content */}
                        <div className="flex-1 min-w-0">
                            {['code', 'code_csharp'].includes(option.content_type) ? (
                                <div className="rounded overflow-hidden">
                                    <CodeMirror
                                        value={option.value}
                                        theme={isDark ? oneDark : lightTheme}
                                        extensions={[cpp()]}
                                        height="auto"
                                        editable={false}
                                        basicSetup={{
                                            lineNumbers: false,
                                            foldGutter: false,
                                        }}
                                    />
                                </div>
                            ) : (
                                <span className="text-gray-800 dark:text-gray-200">
                                    {option.value}
                                </span>
                            )}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default QuestionMCQSingle;
